import React from "react";
import { Link } from "react-router-dom";

const ArrowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4 text-fut-primary shrink-0 mt-1" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Home = ({ translations = {} }) => {
  const isSpanish = translations?.header?.home === "Inicio";

  const t = isSpanish
    ? {
        eyebrow: "San Jose Obrero LLC",
        title:
          "Ordenamos tu negocio antes de declarar, estructurar o prepararte para capital.",
        subtitle:
          "Ayudamos a emprendedores y pequeños negocios latinos a revisar impuestos, estructura empresarial, documentos y preparación financiera con una ruta clara.",
        primaryCta: "Solicitar evaluación",
        secondaryCta: "Ver paquetes",
        authorityTitle: "Autoridad basada en proceso, no en promesas.",
        authorityText:
          "Antes de recomendar un servicio, revisamos tu situación, documentos, estructura y objetivo principal. Así evitamos improvisación, presión innecesaria y decisiones tomadas sin claridad.",
        reviewTitle: "Qué revisamos antes de recomendar una ruta",
        reviewItems: [
          "Estructura del negocio",
          "Situación fiscal",
          "Documentos disponibles",
          "Perfil empresarial",
          "Siguiente paso recomendado",
        ],
        routesTitle: "Elige cómo quieres empezar",
        routes: [
          {
            title: "Paquetes por etapa",
            text:
              "Rutas diseñadas para negocios que necesitan claridad antes de decidir qué servicio tomar.",
            button: "Ver paquetes",
            path: "/packages",
          },
          {
            title: "Servicios específicos",
            text:
              "Soluciones para quien ya sabe qué necesita: impuestos, estructura, perfil empresarial o mapa de capital.",
            button: "Ver servicios",
            path: "/services",
          },
          {
            title: "Evaluación inicial",
            text:
              "La mejor opción si no estás seguro de qué necesita tu negocio primero.",
            button: "Solicitar evaluación",
            path: "/contact",
          },
        ],
        areasTitle: "Áreas que trabajamos",
        areas: [
          "Impuestos personales y small business",
          "Impuestos corporativos y LLC",
          "Estructura empresarial",
          "Perfil empresarial y preparación financiera",
          "Mapa de capital",
          "Educación financiera y revisión de perfil",
        ],
        processTitle: "Nuestra forma de trabajar",
        process: [
          "Escuchamos tu situación.",
          "Revisamos documentos y estructura.",
          "Identificamos la ruta correcta.",
          "Te damos un siguiente paso claro.",
          "Avanzamos solo con lo que tiene sentido.",
        ],
        transparencyTitle: "Trabajamos con claridad",
        transparencyItems: [
          "No prometemos aprobaciones.",
          "No recomendamos rutas sin revisar el caso.",
          "No usamos lenguaje confuso para presionar una decisión.",
          "No convertimos cada situación en el mismo paquete.",
        ],
        finalTitle: "Empieza con una evaluación inicial",
        finalText:
          "Revisamos tu situación y te indicamos qué ruta tiene más sentido antes de avanzar.",
        finalCta: "Solicitar evaluación",
        callLabel: "Llamar",
        whatsappLabel: "WhatsApp",
      }
    : {
        eyebrow: "San Jose Obrero LLC",
        title:
          "We organize your business before filing, structuring, or preparing for capital.",
        subtitle:
          "We help Latino entrepreneurs and small business owners review taxes, business structure, documents, and financial readiness with a clear route.",
        primaryCta: "Request evaluation",
        secondaryCta: "View packages",
        authorityTitle: "Authority built on process, not promises.",
        authorityText:
          "Before recommending a service, we review your situation, documents, structure, and main objective. This helps avoid improvisation, unnecessary pressure, and unclear decisions.",
        reviewTitle: "What we review before recommending a route",
        reviewItems: [
          "Business structure",
          "Tax situation",
          "Available documents",
          "Business profile",
          "Recommended next step",
        ],
        routesTitle: "Choose how you want to start",
        routes: [
          {
            title: "Stage-based packages",
            text:
              "Routes designed for businesses that need clarity before deciding which service to take.",
            button: "View packages",
            path: "/packages",
          },
          {
            title: "Specific services",
            text:
              "Solutions for those who already know what they need: taxes, structure, business profile, or capital map.",
            button: "View services",
            path: "/services",
          },
          {
            title: "Initial evaluation",
            text:
              "The best option if you are not sure what your business needs first.",
            button: "Request evaluation",
            path: "/contact",
          },
        ],
        areasTitle: "Areas we work with",
        areas: [
          "Personal and small business taxes",
          "Corporate and LLC taxes",
          "Business structure",
          "Business profile and financial readiness",
          "Capital map",
          "Financial education and profile review",
        ],
        processTitle: "Our way of working",
        process: [
          "We listen to your situation.",
          "We review documents and structure.",
          "We identify the correct route.",
          "We give you a clear next step.",
          "We move forward only with what makes sense.",
        ],
        transparencyTitle: "We work with clarity",
        transparencyItems: [
          "We do not promise approvals.",
          "We do not recommend routes without reviewing the case.",
          "We do not use confusing language to pressure a decision.",
          "We do not force every situation into the same package.",
        ],
        finalTitle: "Start with an initial evaluation",
        finalText:
          "We review your situation and show you which route makes the most sense before moving forward.",
        finalCta: "Request evaluation",
        callLabel: "Call",
        whatsappLabel: "WhatsApp",
      };

  return (
    <main className="min-h-screen bg-[#050B14] text-fut-text-light font-sans pb-20 md:pb-0 overflow-hidden">
      <section className="relative px-6 pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_14%,rgba(0,212,255,.28),transparent_26%),radial-gradient(circle_at_88%_24%,rgba(59,130,246,.20),transparent_28%),linear-gradient(135deg,#001A33_0%,#06111f_45%,#020617_100%)]" />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-fut-primary/10 blur-3xl" />
        <div className="absolute -bottom-48 -left-32 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative container mx-auto max-w-screen-xl">
          {/* HERO FLUIDO */}
          <div className="grid lg:grid-cols-[1fr_.95fr] gap-10 items-center mb-20">
            <div className="relative z-10">
              <p className="text-fut-primary font-bold uppercase tracking-[.22em] mb-5">
                {t.eyebrow}
              </p>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-black leading-[1.02] mb-6 text-white">
                {t.title}
              </h1>

              <p className="text-lg md:text-xl text-fut-text-light/85 leading-relaxed max-w-3xl mb-8">
                {t.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-fut-primary text-fut-bg-dark px-7 py-4 rounded-2xl font-black shadow-xl shadow-fut-primary/12"
                >
                  {t.primaryCta}
                  <ArrowIcon />
                </Link>

                <Link
                  to="/packages"
                  className="inline-flex items-center justify-center gap-2 border border-fut-primary text-fut-primary px-7 py-4 rounded-2xl font-bold bg-white/5"
                >
                  {t.secondaryCta}
                </Link>
              </div>
            </div>

            <div className="relative min-h-[520px]">
              <div className="absolute -top-8 right-4 w-72 h-72 rounded-full bg-fut-primary/15 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-blue-500/15 blur-3xl" />

              <div className="absolute right-0 top-0 w-[88%] h-[420px] md:h-[500px] rounded-[44%_56%_50%_50%/45%_42%_58%_55%] overflow-hidden shadow-2xl shadow-fut-primary/12">
                <img
                  src="/images/businessman-1.webp"
                  alt="San Jose Obrero LLC"
                  className="w-full h-full object-cover object-center opacity-95"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001A33]/90 via-[#001A33]/15 to-transparent" />
              </div>

              <div className="absolute left-0 bottom-6 max-w-sm rounded-[2.35rem] bg-black/28 backdrop-blur-xl p-6 shadow-xl shadow-fut-primary/8">
                <p className="text-fut-primary font-black uppercase tracking-[.18em] text-xs mb-3">
                  {t.authorityTitle}
                </p>
                <p className="text-fut-text-light/90 leading-relaxed">
                  {t.authorityText}
                </p>
              </div>
            </div>
          </div>

          {/* WHAT WE REVIEW */}
          <div className="mb-20">
            <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-4">
              {t.reviewTitle}
            </p>

            <div className="grid md:grid-cols-5 gap-4">
              {t.reviewItems.map((item) => (
                <div key={item} className="group relative min-h-[130px] rounded-[2.35rem] bg-white/[.055] p-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-fut-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative z-10">
                    <CheckIcon />
                    <p className="text-white font-black mt-4 leading-tight">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ROUTES */}
          <div className="mb-20">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
              <h2 className="text-3xl md:text-5xl font-display font-black leading-tight text-white max-w-3xl">
                {t.routesTitle}
              </h2>

              <p className="text-fut-text-light/70 max-w-md">
                {isSpanish
                  ? "Puedes empezar por paquetes, servicios o evaluación inicial según tu nivel de claridad."
                  : "You can start with packages, services, or an initial evaluation depending on your level of clarity."}
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              {t.routes.map((route, index) => (
                <article
                  key={route.title}
                  className={`relative rounded-[2.75rem] p-7 min-h-[310px] overflow-hidden ${
                    index === 0
                      ? "bg-fut-primary text-fut-bg-dark"
                      : "bg-white/[.045] text-white"
                  }`}
                >
                  <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
                  <p className={`text-sm font-black uppercase tracking-[.18em] mb-5 ${index === 0 ? "text-fut-bg-dark/70" : "text-fut-primary"}`}>
                    0{index + 1}
                  </p>

                  <h3 className="text-2xl md:text-3xl font-display font-black mb-4">
                    {route.title}
                  </h3>

                  <p className={`${index === 0 ? "text-fut-bg-dark/85" : "text-fut-text-light/80"} leading-relaxed mb-8`}>
                    {route.text}
                  </p>

                  <Link
                    to={route.path}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-black ${
                      index === 0
                        ? "bg-fut-bg-dark text-white"
                        : "bg-fut-primary text-fut-bg-dark"
                    }`}
                  >
                    {route.button}
                    <ArrowIcon />
                  </Link>
                </article>
              ))}
            </div>
          </div>

          {/* AREAS + PROCESS */}
          <div className="relative mb-20">
            <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-white/[.055] to-white/[.025]" />

            <div className="relative z-10 p-6 md:p-10 grid lg:grid-cols-[.9fr_1.1fr] gap-8">
              <div>
                <h2 className="text-3xl md:text-5xl font-display font-black text-white mb-6">
                  {t.areasTitle}
                </h2>

                <div className="grid sm:grid-cols-2 gap-3">
                  {t.areas.map((area) => (
                    <div key={area} className="flex gap-3 rounded-2xl bg-black/18 p-4">
                      <CheckIcon />
                      <p className="text-fut-text-light/85 text-sm leading-relaxed">
                        {area}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-3xl md:text-5xl font-display font-black text-fut-primary mb-6">
                  {t.processTitle}
                </h2>

                <div className="space-y-4">
                  {t.process.map((step, index) => (
                    <div key={step} className="flex gap-4 items-start">
                      <div className="w-10 h-10 rounded-2xl bg-fut-primary text-fut-bg-dark flex items-center justify-center font-black shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-fut-text-light/85 pt-2">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* TRANSPARENCY */}
          <div className="grid lg:grid-cols-[.85fr_1.15fr] gap-8 items-center mb-20">
            <div>
              <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-4">
                {t.transparencyTitle}
              </p>
              <h2 className="text-3xl md:text-5xl font-display font-black text-white leading-tight">
                {isSpanish
                  ? "La confianza también se construye diciendo lo que no prometemos."
                  : "Trust is also built by saying what we do not promise."}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {t.transparencyItems.map((item) => (
                <div key={item} className="flex gap-3 rounded-[2.35rem] bg-white/[.045] p-5">
                  <CheckIcon />
                  <p className="text-fut-text-light/85 text-sm leading-relaxed">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* FINAL CTA */}
          <div className="relative rounded-[3rem] overflow-hidden">
            <div className="absolute inset-0 bg-fut-primary" />
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
            <div className="relative z-10 p-8 md:p-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8 text-fut-bg-dark">
              <div>
                <h2 className="text-3xl md:text-5xl font-display font-black leading-tight mb-3">
                  {t.finalTitle}
                </h2>
                <p className="font-semibold opacity-90 max-w-3xl">
                  {t.finalText}
                </p>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-bg-dark text-white px-7 py-4 font-black shrink-0"
              >
                {t.finalCta}
                <ArrowIcon />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-2 md:hidden border-t border-fut-primary/30">
        <a
          href="tel:+18884536026"
          className="bg-white text-fut-bg-dark py-4 text-center font-black"
        >
          {t.callLabel}
        </a>

        <a
          href="https://wa.me/19495687938"
          className="bg-fut-primary text-fut-bg-dark py-4 text-center font-black"
        >
          {t.whatsappLabel}
        </a>
      </div>
    </main>
  );
};

export default Home;
